import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { LoginRequest } from '../models/login-request.model'
import { environment } from '../../../environments/environment'
import { LoginResponse } from '../models/login-response.model'
import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { UserModel } from '../models/user.model'
import { JwtHelperService } from '@auth0/angular-jwt'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl
  private webUrl = environment.webUrl
  private http = inject(HttpClient)
  private jwtHelper = new JwtHelperService()
  private currentUserSubject = new BehaviorSubject<any>(null)
  public currentUser$ = this.currentUserSubject.asObservable()

  constructor() {
    // Tenter de récupérer l'utilisateur depuis le token au démarrage
    this.loadCurrentUserFromStorage()
  }

  login(loginrequest: LoginRequest) {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/authenticate`, loginrequest)
      .pipe(
        tap((response) => {
          if (response && response.accessToken) {
            localStorage.setItem('user', JSON.stringify(response))
            this.loadCurrentUserFromStorage()
          }
        })
      )
  }

  loginWithMicrosoft() {
    return this.http.get<LoginResponse>(`${this.webUrl}/login-with-microsoft`)
  }

  // Récupère l'utilisateur actuel depuis le serveur ou le token
  getCurrentUser(): Observable<any> {
    // Si nous avons déjà l'utilisateur en cache, le retourner
    if (this.currentUserSubject.value) {
      return of(this.currentUserSubject.value)
    }

    // Essayer de charger depuis le local storage
    const userFromStorage = this.loadCurrentUserFromStorage()
    if (userFromStorage) {
      return of(userFromStorage)
    }

    // Si pas d'utilisateur en cache ni dans le local storage, le récupérer depuis le serveur
    return this.http.get<UserModel>(`${this.apiUrl}/auth/me`).pipe(
      tap((user) => {
        this.currentUserSubject.next(user)
      }),
      catchError((err) => {
        console.error('Error fetching current user:', err)
        return throwError(() => new Error('Failed to retrieve current user'))
      })
    )
  }

  // Extrait les données utilisateur du token JWT
  private extractUserFromToken(token: string): any | null {
    try {
      // Utilise JwtHelperService pour décoder le token et récupérer les données utilisateur
      const decodedToken = this.jwtHelper.decodeToken(token)

      // Créer un objet utilisateur à partir des informations du token
      const user = {
        id: decodedToken.userId || decodedToken.sub,
        email: decodedToken.email || decodedToken.sub,
        firstname: decodedToken.firstname,
        lastname: decodedToken.lastname,
        // Autres propriétés selon votre structure de token
      }

      return user
    } catch (e) {
      console.error('Error decoding token:', e)
      return null
    }
  }

  // Charge l'utilisateur depuis le token stocké dans le local storage
  private loadCurrentUserFromStorage(): UserModel | null {
    try {
      const userDataStr = localStorage.getItem('auth.token')
      if (userDataStr) {
        const userData = JSON.parse(userDataStr)

        if (userData) {
          // Extrait l'utilisateur du token
          const user = this.extractUserFromToken(userData)
          if (user) {
            this.currentUserSubject.next(user)
            return user
          }
        }
      }
    } catch (e) {
      console.error('Error loading user from storage:', e)
    }
    return null
  }

  // Vérifie si l'utilisateur est connecté
  isLoggedIn(): boolean {
    const userData = localStorage.getItem('user')
    if (!userData) return false

    try {
      const { token } = JSON.parse(userData)
      return token && !this.jwtHelper.isTokenExpired(token)
    } catch (e) {
      return false
    }
  }

  // Déconnexion
  logout(): void {
    localStorage.removeItem('user')
    this.currentUserSubject.next(null)
  }

  // Récupère le token d'authentification
  getAuthToken(): string | null {
    try {
      const userData = localStorage.getItem('user')
      if (userData) {
        const { token } = JSON.parse(userData)
        return token
      }
      return null
    } catch (e) {
      return null
    }
  }

  // isTokenExpired
  isTokenExpired(token: string): boolean {
    return this.jwtHelper.isTokenExpired(token)
  }
}
